import { z } from 'zod';

import { slugSchema } from './admin.js';

/**
 * Contratos de la ingesta (F2, §6 del plan). Los consumen:
 *  - la API (`/api/ingest/*`, sin sesión: secreto por conexión o API key de ingesta), y
 *  - el worker (payloads de las colas y mapeo de eventos de Instantly).
 */

// ------------------------------------------------------------------ Colas (API → worker)

/** Nombres de las colas BullMQ compartidos entre la API (que encola) y el worker (que procesa). */
export const QUEUE_NAMES = {
  instantlyEvent: 'ingest.instantly-event',
  instantlySweep: 'ingest.instantly-sweep',
  instantlyReconcile: 'instantly.reconcile',
  instantlyAnalytics: 'instantly.nightly-analytics',
  tenantDbSync: 'sync.tenant-db',
  n8nExecutions: 'ops.n8n-executions-poll',
  heartbeatWatchdog: 'ops.heartbeat-watchdog',
  alertDispatch: 'alerts.dispatch',
  partitions: 'maintenance.partitions',
  billingDaily: 'billing.daily',
  usageMeter: 'usage.meter',
  promptsLegacySync: 'prompts.legacy-sync',
  instantlyLeadsSync: 'instantly.leads-sync',
  instantlyEmailsSync: 'instantly.emails-sync',
} as const;
/** Prefijo de Redis de todas las colas (lo usan la API y el worker). */
export const QUEUE_PREFIX = 'platform';

export const instantlyEventJobSchema = z.object({ rawId: z.uuid() });
export type InstantlyEventJob = z.infer<typeof instantlyEventJobSchema>;

// ------------------------------------------------------------------ Instantly

/** Header con el secreto propio de cada conexión (Instantly no firma con HMAC). */
export const INGEST_TOKEN_HEADER = 'x-ingest-token';

const numberish = z
  .union([z.number(), z.string().regex(/^-?\d+$/)])
  .transform((v) => Number(v))
  .nullish();

/**
 * Payload de un webhook de Instantly (guía "Webhook events" + lo observado en producción):
 * base `timestamp, event_type, workspace, campaign_id, campaign_name`; opcionales de lead, step y
 * email. Pueden venir campos extra del lead (merge de su base) → objeto abierto.
 * Gotchas: `email_bounced` NO trae `email_id`; `email_id` identifica el MENSAJE, no el hilo;
 * `auto_reply_received`/`link_clicked` de la guía llegan como `reply_received` (+ is_auto_reply) y
 * `email_link_clicked`.
 */
export const instantlyWebhookPayloadSchema = z.looseObject({
  timestamp: z.string().min(1),
  event_type: z.string().min(1).max(120),
  workspace: z.string().nullish(),
  campaign_id: z.string().nullish(),
  campaign_name: z.string().nullish(),
  lead_email: z.string().nullish(),
  email_account: z.string().nullish(),
  unibox_url: z.string().nullish(),
  step: numberish,
  variant: numberish,
  is_first: z.boolean().nullish(),
  is_auto_reply: z.boolean().nullish(),
  email_id: z.string().nullish(),
  email_subject: z.string().nullish(),
  email_text: z.string().nullish(),
  email_html: z.string().nullish(),
  reply_text_snippet: z.string().nullish(),
  reply_subject: z.string().nullish(),
  reply_text: z.string().nullish(),
  reply_html: z.string().nullish(),
});
export type InstantlyWebhookPayload = z.infer<typeof instantlyWebhookPayloadSchema>;

export const ingestAckSchema = z.object({ ok: z.literal(true), id: z.uuid().optional() });

// ------------------------------------------------------------------ Envíos (Gmail / bot / manual)

const SHA256_RE = /^[0-9a-f]{64}$/;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export const emailSentAttachmentSchema = z
  .object({
    filename: z.string().trim().min(1).max(255),
    mime: z.string().trim().min(1).max(120).default('application/octet-stream'),
    /** Contenido en base64 (la primera vez que se manda ese archivo). */
    content_base64: z
      .string()
      .max(Math.ceil((MAX_ATTACHMENT_BYTES * 4) / 3) + 4)
      .optional(),
    /** Referencia a un archivo ya subido (mismo PDF → sólo el hash). */
    sha256: z.string().regex(SHA256_RE, { error: 'sha256 en hex (64 caracteres)' }).optional(),
  })
  .refine((a) => a.content_base64 !== undefined || a.sha256 !== undefined, {
    error: 'Cada adjunto necesita content_base64 o sha256',
  });
export type EmailSentAttachment = z.infer<typeof emailSentAttachmentSchema>;

export const emailSentIngestSchema = z.object({
  /** Obligatorio con una API key de agencia; con una de empresa, si viene tiene que coincidir. */
  tenant: slugSchema.optional(),
  channel: z.enum(['gmail', 'instantly', 'manual']).default('gmail'),
  direction: z.enum(['out', 'in']).default('out'),
  status: z.enum(['sent', 'failed']).default('sent'),
  error: z.string().max(2000).optional(),
  from: z.string().trim().max(320).optional(),
  to: z.string().trim().max(320),
  lead_email: z.string().trim().max(320).optional(),
  subject: z.string().max(1000).optional(),
  body_html: z.string().max(1_000_000).optional(),
  body_text: z.string().max(1_000_000).optional(),
  gmail_message_id: z.string().max(255).optional(),
  rfc_message_id: z.string().max(998).optional(),
  instantly_email_id: z.string().max(255).optional(),
  thread_key: z.string().max(500).optional(),
  /** Id de la campaña en Instantly (externo), si el envío pertenece a una. */
  campaign_id: z.string().max(255).optional(),
  sent_at: z.iso.datetime({ offset: true }).optional(),
  /** Clave propia del emisor para reintentos seguros cuando no hay id de proveedor. */
  idempotency_key: z.string().min(8).max(200).optional(),
  meta: z.record(z.string(), z.unknown()).optional(),
  attachments: z.array(emailSentAttachmentSchema).max(10).default([]),
});
export type EmailSentIngest = z.input<typeof emailSentIngestSchema>;

export const emailSentResponseSchema = z.object({
  ok: z.literal(true),
  messageId: z.uuid(),
  deduplicated: z.boolean(),
  attachments: z.array(
    z.object({
      sha256: z.string(),
      attachmentId: z.uuid(),
      stored: z.enum(['new', 'existing']),
    }),
  ),
});
export type EmailSentResponse = z.infer<typeof emailSentResponseSchema>;

// ------------------------------------------------------------------ Heartbeats de crons

export const heartbeatIngestSchema = z.object({
  /** Clave estable del job, p. ej. `cortacircuitos`, `backup`, `scraper:starsam`. */
  job: z
    .string()
    .trim()
    .regex(/^[a-z0-9][a-z0-9:._-]{0,119}$/, { error: 'Sólo minúsculas, números y : . _ -' }),
  tenant: slugSchema.optional(),
  status: z.enum(['success', 'error', 'warning']).default('success'),
  /** Cada cuánto corre (para el watchdog). Se guarda la primera vez o cuando cambia. */
  expected_interval_seconds: z.coerce
    .number()
    .int()
    .positive()
    .max(31 * 86_400)
    .optional(),
  grace_seconds: z.coerce.number().int().min(0).max(86_400).optional(),
  started_at: z.iso.datetime({ offset: true }).optional(),
  finished_at: z.iso.datetime({ offset: true }).optional(),
  duration_ms: z.coerce.number().int().min(0).optional(),
  instance: z.string().max(120).optional(),
  metrics: z.record(z.string(), z.unknown()).optional(),
  error: z.string().max(4000).optional(),
  log_tail: z.string().max(4000).optional(),
});
export type HeartbeatIngest = z.input<typeof heartbeatIngestSchema>;

// ------------------------------------------------------------------ Ejecuciones de n8n (push opcional)

export const OPS_RUN_STATUSES = [
  'running',
  'success',
  'error',
  'warning',
  'canceled',
  'unknown',
] as const;
export const opsRunStatusSchema = z.enum(OPS_RUN_STATUSES);
export type OpsRunStatus = z.infer<typeof opsRunStatusSchema>;

export const n8nExecutionIngestSchema = z.object({
  tenant: slugSchema.optional(),
  instance: z.string().trim().min(1).max(120),
  workflow: z.string().trim().min(1).max(255),
  execution_id: z.string().trim().min(1).max(64),
  status: z.string().trim().min(1).max(40),
  started_at: z.iso.datetime({ offset: true }),
  finished_at: z.iso.datetime({ offset: true }).optional(),
  error: z.string().max(4000).optional(),
  node: z.string().max(255).optional(),
});
export type N8nExecutionIngest = z.infer<typeof n8nExecutionIngestSchema>;

/** Normaliza los status de n8n (success, error, crashed, canceled, running, waiting, new…). */
export function normalizeN8nStatus(status: string): OpsRunStatus {
  switch (status) {
    case 'success':
      return 'success';
    case 'error':
    case 'crashed':
    case 'failed':
      return 'error';
    case 'canceled':
    case 'cancelled':
      return 'canceled';
    case 'running':
    case 'waiting':
    case 'new':
      return 'running';
    default:
      return 'unknown';
  }
}

// ------------------------------------------------------------------ Config de integraciones (no sensible)

/** `tenant_integrations.config` de una DB vieja (`legacy_db`). El secreto es la connection string. */
export const legacyDbConfigSchema = z.object({
  /** Schema de negocio: `leads` (central, starsam, sweet) o `colectiu`. */
  schema: z.string().regex(/^[a-z_][a-z0-9_]{0,62}$/),
  /** Streams a sincronizar; por defecto todos los que existan en esa DB. */
  streams: z.array(z.string()).optional(),
  /**
   * `reader` (default): la usa el sync. `writer`: credenciales con UPDATE acotado para las acciones
   * de la consola (F3) — el sync la ignora; la usa el adaptador de comandos de la API
   * (platform/docs/legacy-writes.md).
   */
  role: z.enum(['reader', 'writer']).optional(),
  /** Valor de `kb_chunks.tenant` de esta empresa (default: el slug de la empresa). */
  kbTenant: z.string().min(1).max(60).optional(),
});
export type LegacyDbConfig = z.infer<typeof legacyDbConfigSchema>;

/** `tenant_integrations.config` de una instancia n8n. El secreto es la API key (X-N8N-API-KEY). */
export const n8nConfigSchema = z.object({
  baseUrl: z.url(),
  /** Nombre corto de la instancia (`central`, `sweet-papaya`…). */
  instance: z.string().min(1).max(60),
});
export type N8nConfig = z.infer<typeof n8nConfigSchema>;

/** `tenant_integrations.config` de Telegram. El secreto es el token del bot. */
export const telegramConfigSchema = z.object({ chatId: z.string().min(1).max(64) });
export type TelegramConfig = z.infer<typeof telegramConfigSchema>;

// ------------------------------------------------------------------ Documentos para n8n (dossier)

/**
 * `GET /api/ingest/documents/:key/active` — n8n baja la versión ACTIVA de un documento de la
 * empresa (p. ej. el dossier que adjunta el bot). La empresa sale de la API key; con una key de
 * agencia hay que indicar `tenant`. `have` = sha256 de la copia que ya tiene quien pregunta: si
 * coincide con la activa responde 304 sin cuerpo (así el sync horario no baja el PDF cada vez).
 */
export const activeDocumentQuerySchema = z.object({
  tenant: slugSchema.optional(),
  have: z
    .string()
    .regex(/^[0-9a-f]{64}$/, { error: 'sha256 en hex (64 caracteres)' })
    .optional(),
});
export type ActiveDocumentQuery = z.infer<typeof activeDocumentQuerySchema>;

/** Headers con los que se describe la versión servida (también en la respuesta 304). */
export const DOCUMENT_HEADERS = {
  sha256: 'x-document-sha256',
  version: 'x-document-version',
  /** Nombre del archivo, URL-encoded (puede traer espacios y acentos). */
  filename: 'x-document-filename',
} as const;

/**
 * Latido del sync del dossier que corre en el n8n central (workflow «16 — Dossier: sincronizar
 * desde la plataforma»). Además de vigilar que el sync corra, es la ÚNICA fuente de verdad de la
 * plataforma sobre si el asistente de esa empresa tiene activada la respuesta de primer contacto
 * (esa llave vive en el entorno de n8n: `<TENANT>__FIRST_CONTACT_ENABLED`).
 */
export const DOSSIER_SYNC_JOB = 'bot.dossier-sync';

/** `metrics` que manda el sync en su latido (se leen con `safeParse`: nada es obligatorio). */
export const dossierSyncMetricsSchema = z.object({
  first_contact_enabled: z.boolean().optional(),
  /**
   * Qué contesta el asistente al primer contacto interesado (2026-09-24): `template` = el texto
   * fijo «Respuesta de primer contacto con dossier»; `bot_answer` = la respuesta que escribe el
   * asistente con la base de conocimiento, con el dossier adjunto (Star Sam).
   */
  first_contact_mode: z.enum(['template', 'bot_answer']).optional(),
  /** Versión/sha de la copia de respaldo que quedó en el volumen de n8n. */
  local_version: z.number().int().nullable().optional(),
  local_sha256: z.string().nullable().optional(),
  local_filename: z.string().nullable().optional(),
  /** `updated` = se bajó una versión nueva, `unchanged` = ya estaba, `no_document` = no hay activa. */
  result: z.enum(['updated', 'unchanged', 'no_document', 'error']).optional(),
});
export type DossierSyncMetrics = z.infer<typeof dossierSyncMetricsSchema>;
